import {
  QueryV1Flags,
  DeferredQueryInput,
  J1QLResultData,
} from '../../../types/J1QL';
import useJ1QLWithCursorPagination from './useJ1QLWithCursorPagination';
import mergeJ1QLDataResponses from '../utils/mergeJ1QLDataResponses';
import { QueryDataOptions } from '@apollo/client';
import defaults from 'lodash/defaults';
import { formatDeferredQuery } from '../../clients/deferredQuery';
import { parse } from '@jupiterone/query-language-parser';

export type { DeferredQueryInput };

interface UseDeferredJ1QLInput {
  input: DeferredQueryInput;
  dataPath?: string;
  cursorPath?: string;
  skip?: boolean;
}

const useDeferredJ1QLQuery = <T extends J1QLResultData>(
  query: QueryDataOptions['query'],
  { input, dataPath, cursorPath, skip }: UseDeferredJ1QLInput
) => {
  const formattedQuery = formatDeferredQuery(input.query);

  if (!skip && !parse(formattedQuery)) {
    throw new Error('Unable to parse J1QL query');
  }

  const j1QLResult = useJ1QLWithCursorPagination<T>({
    query,
    deferred: true,
    dataPath: dataPath || '',
    cursorPath: cursorPath || 'cursor',
    mergeDataResponses: mergeJ1QLDataResponses,
    variables: {
      ...input.variables,
      query: formattedQuery,
      flags: applyDefaultQueryV1Flags(input?.flags),
      includeDeleted: input?.includeDeleted,
      cursor: input?.cursor,
      remember: input?.remember,
    },
    skip,
    // I cannot stress enough how important this combination of fetch policies is.
    // Apollo Client v3 changed some of the fetch policy behavior, so on a fetchMore
    // a refetch is triggered with the original variables.  The nextFetchPolicy here
    // makes sure we don't make a network call on that erroneous refetch- henry
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'cache-first',
  });

  return {
    ...j1QLResult,
  };
};

/**
 * Applies default values for QueryV1Flags for all queries
 * executed via the 'useDeferredJ1QLQuery' hook.
 *
 * The actual application of these flags on the behavior of
 * J1QL queries are gated by feature toggle on the backend.
 */
function applyDefaultQueryV1Flags(flags?: QueryV1Flags) {
  return defaults(flags, {
    // return "_meta" field on J1QL "table" results
    rowMetadata: true,
    // return computed properties on graph objects
    // to assist with highlighting certain features of an object.
    //
    // For example, a vertex may be tagged with a property
    // that represents that it has a relationship to a Problem entity
    computedProperties: true,
  });
}

export default useDeferredJ1QLQuery;
